/**
 * advance-combat.ts — per-tick combat pass.
 *
 * Runs inside advanceTick AFTER advance-detection (so teamVision is fresh
 * and reflects any stealth-break from firing on a previous tick) and BEFORE
 * the `tick++` increment (so `lastFiredTick` stamped here reads as
 * "fired this tick" to any later pass — detection's FIRING_BREAKS_STEALTH
 * window, e.g. — on the *next* tick).
 *
 * Outline per ship (ascending id for determinism):
 *   1. Skip ships that can't shoot this tick — dead, mid-jump, EMP-disabled,
 *      cloaked-without-Advanced-Cloaking, or with no weapon loadout.
 *   2. For each weapon slot (in template order):
 *        a. Tick the cooldown. If still >0 after decrement, skip slot.
 *        b. Resolve the slot to a concrete WeaponDef (highest-Mk
 *           researched, falling back to Mk 1).
 *        c. EMP weapons (subType === 'EMP') route through `fireEmpSalvo`
 *           instead of `fireSalvo` — disablement rather than HP damage
 *           (MECHANICS §2.4, v3M4 2026-04-28). Range, cooldown, and
 *           lastFiredTick stamping are identical to the regular path.
 *        d. Refuse to fire with empty magazine (Concussion; infinite for
 *           others). Ammo regen runs below so the empty slot gets a tick
 *           to refill.
 *        e. Pick a target via pickTarget at weapon.range * WEAPON_RANGE_SCALE.
 *        f. Fire a salvo of `count` rounds, rolling hit per round, applying
 *           damage-type-modified damage through the shield→armor→hull chain.
 *        g. Stamp lastFiredTick on the attacker (salvo happened regardless
 *           of whether any round hit — firing is what breaks stealth).
 *        h. Decrement magazine by `count` (Concussion only).
 *        i. Set slot cooldown to cooldownTicksFor(weapon.rateOfFire).
 *   3. After all slots processed, regen Concussion magazines at
 *      state.balanceData.combat.ammoRegenPerTick up to weapon.magazine.
 *
 * After the per-ship loop finishes, any ship whose currentHull reached 0
 * is removed via despawnShip — deferred until after all firing so a
 * mid-pass kill doesn't corrupt iteration ordering.
 *
 * v1 simplifications (deliberate — each has a milestone it belongs in):
 *   - All HP-damage weapons resolve hit-scan (no projectile travel time).
 *     Concussion splash for HP damage is still deferred — only the EMP
 *     subType uses splash via {@link fireEmpSalvo} in v3M4.
 *   - No friendly fire on HP weapons (pickTarget already filters to enemies
 *     via teamVision). EMP intentionally affects friendly + enemy ships in
 *     blast per MECHANICS §2.4.
 *
 * v3M5 (2026-05-04) — Advanced Cloaking fire-while-cloaked. RACE-CC ships
 * with `Advanced Cloaking` (Tech Level 6, race-locked at research time)
 * may fire while cloaked WITHOUT breaking cloak. Each fire-tick pays an
 * additional `state.balanceData.detection.cloakFireExtraDrainPerTick` on
 * top of the base cloak drain (deducted here after the firing loop).
 * `advance-cloak.ts`'s next-tick `currentEnergy < cloakEnergyDrainPerTick`
 * guard auto-decloaks once the bumped drain depletes the energy pool.
 * Without Advanced Cloaking, cloaked ships still cannot fire at all.
 */

import { nextFloat } from '../state/rng';
import { ADVANCED_CLOAKING_TECH_ID } from './balance-detection';
import {
  SQUADRON_FIGHTER_COUNT,
  updateSquadronFighterCount,
} from './balance-carrier-squadrons';
import {
  cooldownTicksFor,
  damageTypeModifiers,
  resolveSlotWeapon,
} from './combat-balance';
import { hitChance } from './combat';
import { pickTarget } from './targeting';
import { effectiveArmor, effectiveMaxHull } from './research-effects';
import { empDisableTicksForTarget } from './balance-emp';
import { auraBonusesForShip } from './auras';
import { enqueueMissile } from './advance-missiles';
import type {
  GameState,
  ShipInstance,
  ShipInstanceId,
  ShipTemplate,
  WeaponDef,
  WeaponSlotState,
} from '../state/types';

/** Advance combat by exactly one tick. Mutates state. */
export function advanceCombat(state: GameState): void {
  // v3M19 — clear the per-tick combat-event buffer. Anything that
  // wants to publish a damage-number floater (combat pass, plus the
  // later Surface Battery + ODP passes that share `applyVolleyToShip`)
  // pushes here; the renderer drains between sim ticks. Reset is safe
  // because the renderer's `lastObservedTick` gate guarantees the prior
  // tick's events were scheduled into the VFX queue before this clear.
  state.combatEvents = [];

  const ships = [...state.ships.values()].sort((a, b) => a.id - b.id);

  for (const ship of ships) {
    processShipSlots(state, ship);
  }

  // Cull dead ships after all firing. Deferring keeps mid-pass iteration
  // from having to tolerate concurrent mutations of state.ships.
  const dead: ShipInstanceId[] = [];
  for (const ship of state.ships.values()) {
    if (ship.currentHull <= 0) dead.push(ship.id);
  }
  // Sort for deterministic scrub order; dead ids are removed one-by-one
  // from every team's vision map inside despawnShip.
  dead.sort((a, b) => a - b);
  for (const id of dead) despawnShip(state, id);
}

function processShipSlots(state: GameState, ship: ShipInstance): void {
  if (ship.weaponSlots.length === 0) return;
  // Empire lookup moves before canFire so the cloak gate can read
  // `researchedTechs.has(ADVANCED_CLOAKING_TECH_ID)`. A missing empire
  // record is treated like the legacy short-circuit — return without
  // ticking cooldowns (preserves prior behaviour for orphaned ships).
  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) return;
  const canFireWhileCloaked = empire.researchedTechs.has(ADVANCED_CLOAKING_TECH_ID);
  if (!canFire(ship, canFireWhileCloaked)) {
    tickCooldownsOnly(ship);
    return;
  }
  const tmpl = getTemplate(state, ship);
  if (!tmpl) return;
  if (tmpl.weaponLoadout.length === 0) return;

  const loadout = tmpl.weaponLoadout;
  const firedThisTick = new Set<number>();
  // MFix8.5 — friendly aura damageMult on the attacker. Computed once
  // per ship-tick (auras don't change between this ship's slots) and
  // passed through fireSalvo → applyVolleyToShip. EMP isn't a damage
  // weapon, so the EMP path ignores it.
  const attackerAuraDamageMult = auraBonusesForShip(state, ship).damageMult;

  for (let i = 0; i < loadout.length; i++) {
    const slotTmpl = loadout[i]!;
    const slot = ship.weaponSlots[i];
    if (!slot) continue;

    if (slot.cooldownTicks > 0) {
      slot.cooldownTicks -= 1;
      continue;
    }

    const weapon = resolveSlotWeapon(
      slotTmpl.weapon,
      state.weapons,
      empire.researchedTechs,
    );
    if (!weapon) continue;
    if (slot.ammoRemaining <= 0) continue;

    const rangeEff = weapon.range * state.balanceData.combat.weaponRangeScale;
    const pick = pickTarget(state, ship, rangeEff);
    if (!pick) continue;

    if (weapon.subType === 'EMP') {
      fireEmpSalvo(state, ship, pick.ship, weapon);
    } else {
      fireSalvo(state, ship, pick.ship, slotTmpl.count, weapon, attackerAuraDamageMult);
    }

    ship.lastFiredTick = state.tick;
    // v2M7.4a: record target so the render layer can draw a tracer
    // line from firer to target each frame the fire happened.
    ship.lastFiredTargetShipId = pick.ship.id;
    if (Number.isFinite(slot.ammoRemaining)) {
      slot.ammoRemaining = Math.max(0, slot.ammoRemaining - slotTmpl.count);
    }
    slot.cooldownTicks = cooldownTicksFor(weapon.rateOfFire);
    firedThisTick.add(i);
  }

  regenAmmo(state, ship, loadout, state.weapons, empire.researchedTechs, firedThisTick);

  // v3M5 — fire-while-cloaked extra energy drain. The ship is cloaked and
  // fired ≥1 slot this tick; deduct the configured extra drain on top of
  // the base cloak drain that ran in advance-cloak earlier this tick.
  // Floor at 0 — auto-decloak falls out of advance-cloak's next-tick
  // `currentEnergy < cloakEnergyDrainPerTick` check.
  if (ship.cloaked && firedThisTick.size > 0) {
    ship.currentEnergy = Math.max(
      0,
      ship.currentEnergy - state.balanceData.detection.cloakFireExtraDrainPerTick,
    );
  }
}

function canFire(ship: ShipInstance, canFireWhileCloaked: boolean): boolean {
  if (ship.currentHull <= 0) return false;
  if (ship.systemId === null) return false;
  if (ship.disabledTicks > 0) return false;
  // v3M5 — cloaked ships can fire only when the empire has researched
  // Advanced Cloaking (RACE-CC L6, MECHANICS §2.1). Race-locking on the
  // tech itself means non-CC empires never reach this branch; the gate
  // is uniform here so the test surface stays simple.
  if (ship.cloaked && !canFireWhileCloaked) return false;
  return true;
}

/**
 * For ships that can't fire this tick (cloaked-without-Advanced-Cloaking,
 * EMP-disabled, mid-jump), we still tick weapon cooldowns down so they
 * come out of the pause ready to engage rather than artificially
 * sluggish. Matches how EMP and cloak energy drain behave — the rest of
 * the ship's systems keep running.
 */
function tickCooldownsOnly(ship: ShipInstance): void {
  for (const slot of ship.weaponSlots) {
    if (slot.cooldownTicks > 0) slot.cooldownTicks -= 1;
  }
}

/**
 * Regen Concussion magazines at +state.balanceData.combat.ammoRegenPerTick per idle tick, capped
 * at weapon.magazine.
 *
 * A slot that *fired* this tick is skipped — firing and regen are mutually
 * exclusive per tick, which gives the magazine its front-loaded burst
 * character: you empty it fast, then throttle to the regen rate while the
 * weapon cycles. Without this, a rate-of-fire-0.5 weapon (cooldown 2)
 * would always full-regen between shots and magazine size would never
 * matter (MECHANICS §3.1.1 / §3.1.2).
 *
 * Non-Concussion slots have Infinity ammo and are skipped by the
 * `Number.isFinite` check — the regen loop never touches them.
 */
function regenAmmo(
  state: GameState,
  ship: ShipInstance,
  loadout: ReadonlyArray<{ weapon: string; count: number }>,
  weapons: ReadonlyMap<string, WeaponDef>,
  researchedTechs: ReadonlySet<string>,
  firedThisTick: ReadonlySet<number>,
): void {
  for (let i = 0; i < loadout.length; i++) {
    if (firedThisTick.has(i)) continue;
    const slotTmpl = loadout[i]!;
    const slot = ship.weaponSlots[i];
    if (!slot) continue;
    if (!Number.isFinite(slot.ammoRemaining)) continue;
    const weapon = resolveSlotWeapon(slotTmpl.weapon, weapons, researchedTechs);
    if (!weapon || weapon.magazine === null) continue;
    slot.ammoRemaining = Math.min(
      weapon.magazine,
      slot.ammoRemaining + state.balanceData.combat.ammoRegenPerTick,
    );
  }
}

/**
 * Fire `count` rounds from `attacker` at `target`. Each round:
 *   - rolls an independent to-hit against state.rng (single shared stream,
 *     so combat is reproducible under seed)
 *   - on hit, splits raw damage into the portion that would land on shields
 *     vs the portion that overflows to hull, scales each portion by the
 *     weapon's damage-type modifier, then applies the hull portion through
 *     (1 - armor%) reduction
 *
 * Why split-then-scale (rather than two calls to combat.resolveDamage): the
 * damage-type modifier is *per layer* — Energy is +shield/-hull, Kinetic
 * is -shield/+hull. A single pre-scale on the raw number can't express that
 * asymmetry, so the combat pass inlines its own shield→armor→hull walk.
 * combat.resolveDamage stays useful as the bias-free primitive (its own
 * tests still cover it).
 */
function fireSalvo(
  state: GameState,
  attacker: ShipInstance,
  target: ShipInstance,
  count: number,
  weapon: WeaponDef,
  damageMult: number,
): void {
  // v3M28.2 — Missile-subType weapons enqueue persistent `Missile`
  // entities at fire-time and resolve damage on impact (see
  // `advance-missiles.ts`). `damageMult` is intentionally dropped on
  // the missile path: the v3M28.0 design lock froze accuracy at fire
  // but resolves damage on impact from the live catalogue + target
  // empire's tech, NOT from the attacker's friendly-aura buff at
  // launch time. Aura/buff propagation into in-flight missiles is
  // parked for v4 escalation.
  if (weapon.subType === 'Missile') {
    for (let i = 0; i < count; i++) {
      enqueueMissile(state, attacker, target, weapon);
    }
    return;
  }
  applyVolleyToShip(state, target, count, weapon, damageMult, attacker.ownerEmpireId);
}

/**
 * Per-round volley applied to a ship target — each round rolls accuracy
 * vs the target's Dodge, splits raw damage into shield-vs-hull portions,
 * scales each by the weapon's damage-type modifier, and runs the hull
 * portion through (1 - armor%) reduction. On hull ≤ 0 returns early
 * (overkill rounds skipped).
 *
 * Extracted from `fireSalvo` so v2M6.5 Surface Batteries can fire at
 * ships with the exact same damage chain as ship-vs-ship combat — a
 * battery is "an attacker without a ship" and the attacker reference
 * was unused anyway.
 */
export function applyVolleyToShip(
  state: GameState,
  target: ShipInstance,
  count: number,
  weapon: WeaponDef,
  /**
   * MFix8.5 — multiplicative damage scalar applied to `weapon.damage`
   * before the shield/armor/hull split. Used by the combat pass to fold
   * the attacker's friendly-aura `damageMult` (e.g. Shaman's
   * weapon-damage buff) into every round of the volley. Defaults to 1
   * so call sites that don't carry an attacker context (Surface
   * Batteries, tests) keep their existing behaviour unchanged.
   */
  damageMult: number = 1,
  /**
   * v3M19 — firing empire's id, captured on the emitted DamageEvent so
   * the renderer can later flip a "always show damage I dealt" override
   * by checking `ownerEmpireId === viewer`. Optional; when omitted the
   * event records the target's own empire (a harmless lie since the
   * field isn't load-bearing for the v3M19 FoW gate, which keys on
   * `targetShipId` visibility instead).
   */
  attackerEmpireId?: number,
): void {
  const targetTmpl = getTemplate(state, target);
  const targetEmpire = state.empires.get(target.ownerEmpireId);
  // Armor is empire-wide tech + chassis baseline. Read live each hit so
  // Armor Mk research immediately affects damage taken by every ship of
  // the empire — no spawn-time capture.
  const armor =
    targetTmpl && targetEmpire
      ? effectiveArmor(state, targetEmpire, targetTmpl)
      : targetTmpl?.armor ?? 0;
  const dodge = targetTmpl?.dodge ?? 0;
  const hitP = hitChance(weapon.accuracy, dodge);
  const mods = damageTypeModifiers(state.balanceData)[weapon.damageType];
  const armorFactor = Math.max(0, Math.min(1, armor));
  const scaledDamage = weapon.damage * damageMult;

  // v3M19 — accumulate per-volley tally so the floating-damage-numbers
  // dispatch can emit one DamageEvent per slot fire (regardless of
  // round count). Tracked locally rather than mutating state.combatEvents
  // per round so partial-volley early-returns (target destroyed mid-
  // salvo) still publish a correctly-summed event.
  let shieldDamageTotal = 0;
  let hullDamageTotal = 0;
  let hitCount = 0;
  let missCount = 0;

  for (let k = 0; k < count; k++) {
    const roll = nextFloat(state.rng);
    if (roll >= hitP) {
      missCount++;
      continue;
    }
    hitCount++;

    const rawShieldHit = Math.min(scaledDamage, target.currentShields);
    const rawHullHit = Math.max(0, scaledDamage - rawShieldHit);

    const scaledShield = rawShieldHit * mods.shield;
    const scaledPostShield = rawHullHit * mods.hull;
    const hullDamage = scaledPostShield * (1 - armorFactor);

    shieldDamageTotal += scaledShield;
    hullDamageTotal += hullDamage;

    target.currentShields = Math.max(0, target.currentShields - scaledShield);
    target.currentHull = Math.max(0, target.currentHull - hullDamage);

    // v3M17.3 — squadron variable-count HP pool. After hull is updated,
    // recompute liveFighterCount from the post-damage pool. The bucket
    // helper is a no-op for non-squadrons (target.liveFighterCount ===
    // undefined). Per-fighter hull = full squadron pool /
    // SQUADRON_FIGHTER_COUNT — uses the empire's effective max hull
    // (which folds in Hull Mk multipliers) so squadron damage tracks
    // the same scaling as the parent fighter.
    if (target.liveFighterCount !== undefined && targetTmpl && targetEmpire) {
      const fullPool = effectiveMaxHull(state, targetEmpire, targetTmpl);
      updateSquadronFighterCount(target, fullPool / SQUADRON_FIGHTER_COUNT);
    }

    if (target.currentHull <= 0) {
      // Target already destroyed — any remaining rounds in this salvo are
      // "overkill" and skipped. Matches intuition (once the ship explodes,
      // the rest of the burst misses an absent target).
      pushDamageEventIfApplicable(
        state,
        target,
        weapon,
        attackerEmpireId,
        shieldDamageTotal,
        hullDamageTotal,
        hitCount,
        missCount,
      );
      return;
    }
  }

  pushDamageEventIfApplicable(
    state,
    target,
    weapon,
    attackerEmpireId,
    shieldDamageTotal,
    hullDamageTotal,
    hitCount,
    missCount,
  );
}

/**
 * v3M19 — push one DamageEvent summarising the volley if the target is
 * in-system (mid-jump targets can't anchor a floater) and at least one
 * round resolved (hit or miss). Suppressed entirely on count=0 corner
 * cases so downstream consumers don't have to filter empty rows.
 */
function pushDamageEventIfApplicable(
  state: GameState,
  target: ShipInstance,
  weapon: WeaponDef,
  attackerEmpireId: number | undefined,
  shieldDamage: number,
  hullDamage: number,
  hitCount: number,
  missCount: number,
): void {
  if (hitCount === 0 && missCount === 0) return;
  if (target.systemId === null) return;
  // applyVolleyToShip is only called for HP-damage weapons (Energy /
  // Kinetic / Concussion); EMP weapons take fireEmpSalvo, so the
  // 'EMP' arm of WeaponDef.damageType cannot reach this code at
  // runtime. The narrow check lets us also skip the push defensively
  // if a future caller routes an EMP weapon here by mistake — silently
  // dropping the event is preferable to a render-time crash.
  if (
    weapon.damageType !== 'Energy' &&
    weapon.damageType !== 'Kinetic' &&
    weapon.damageType !== 'Concussion'
  ) {
    return;
  }
  state.combatEvents.push({
    targetShipId: target.id,
    ownerEmpireId: attackerEmpireId ?? target.ownerEmpireId,
    systemId: target.systemId,
    sx: target.sx,
    sy: target.sy,
    shieldDamage,
    hullDamage,
    hitCount,
    missCount,
    damageType: weapon.damageType,
  });
}

function getTemplate(state: GameState, ship: ShipInstance): ShipTemplate | undefined {
  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) return undefined;
  return state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
}

/**
 * Fire an EMP weapon: roll one hit against the picked target, and if it
 * lands, apply a disable to every ship (friendly + enemy) within the
 * weapon's blast radius around the target's position. Per-target disable
 * duration = `state.balanceData.emp.baseDisableTicks − effectiveEmpProtection`, floored
 * at 0. A target already disabled for longer keeps its remaining time —
 * EMP does not shorten an existing disable.
 *
 * Friendly fire is intentional per MECHANICS §2.4: "the disable affects
 * friendly and enemy ships equally". Cloak does not protect a target
 * from EMP — the blast is electromagnetic and reaches stealthed ships.
 *
 * Blast radius falls back to `state.balanceData.emp.defaultBlastRadius` when
 * `weapon.blastRadius` is null (the v1 EMP weapon ships with null in
 * `Ships.xlsx`; the default produces a meaningful effect without a data
 * round-trip). Mid-jump ships (`systemId === null`) are skipped since
 * they're in hyperspace.
 *
 * `attacker` is unused for damage but kept in the signature for symmetry
 * with `fireSalvo` and to leave room for future "EMP target excludes
 * attacker" or per-attacker accuracy bonuses in v3M10 balance.
 */
function fireEmpSalvo(
  state: GameState,
  _attacker: ShipInstance,
  target: ShipInstance,
  weapon: WeaponDef,
): void {
  // Hit roll. EMP weapons' v1 accuracy is 1.0, so this is a formality —
  // but kept structurally identical to fireSalvo so a v3M10 balance pass
  // can drop accuracy below 1.0 without touching the call site.
  const targetTmpl = getTemplate(state, target);
  const dodge = targetTmpl?.dodge ?? 0;
  const hitP = hitChance(weapon.accuracy, dodge);
  const roll = nextFloat(state.rng);
  if (roll >= hitP) return;

  const blastRadius =
    (weapon.blastRadius ?? state.balanceData.emp.defaultBlastRadius) * state.balanceData.combat.weaponRangeScale;
  const blastRadiusSq = blastRadius * blastRadius;
  const cx = target.sx;
  const cy = target.sy;

  for (const ship of state.ships.values()) {
    if (ship.systemId !== target.systemId) continue;
    if (ship.systemId === null) continue; // mid-jump (defensive)
    if (ship.currentHull <= 0) continue;
    const dx = ship.sx - cx;
    const dy = ship.sy - cy;
    if (dx * dx + dy * dy > blastRadiusSq) continue;
    const empire = state.empires.get(ship.ownerEmpireId);
    const tmpl = empire
      ? state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`)
      : undefined;
    if (!empire || !tmpl) continue;
    const ticks = empDisableTicksForTarget(state, empire, tmpl);
    if (ticks <= 0) continue;
    if (ship.disabledTicks < ticks) ship.disabledTicks = ticks;
  }
}

/**
 * Despawn a ship cleanly. Called by the combat pass when currentHull ≤ 0,
 * but exported so other future paths (disband, self-destruct, colony ship
 * consumed on use) can share the same teardown:
 *
 *   - Remove from `state.ships`.
 *   - Remove from the owning empire's `shipIds` array.
 *   - Scrub from every team's vision map. The grace window is for losing
 *     contact with a live ship; a destroyed ship shouldn't linger as a
 *     ghost dot forever, and pickTarget would otherwise keep trying to
 *     aim at a ship that isn't in state.ships anymore.
 */
export function despawnShip(state: GameState, id: ShipInstanceId): void {
  state.ships.delete(id);
  for (const empire of state.empires.values()) {
    const idx = empire.shipIds.indexOf(id);
    if (idx >= 0) empire.shipIds.splice(idx, 1);
  }
  for (const teamMap of state.teamVision.values()) {
    teamMap.delete(id);
  }
  // v3M18 — sever any Warp Gate connection that referenced this ship.
  // The link Map is small; this is a cheap O(N) scan with N typically
  // < 10. Idempotent — non-gate ships scan with no matches. Inlined
  // (rather than imported from commands.ts) to avoid the circular
  // import — commands.ts already imports despawnShip from this file.
  const deadLinks: number[] = [];
  for (const link of state.warpGateLinks.values()) {
    if (link.gateAId === id || link.gateBId === id) deadLinks.push(link.id);
  }
  for (const linkId of deadLinks) state.warpGateLinks.delete(linkId);
  // v3M17.5 — squadron sever-on-carrier-destroy. When a carrier dies,
  // every squadron parented to it despawns instantly (no orphans). Same
  // inlined-scan pattern as the warp-gate sever above to avoid the
  // advance-carrier-squadrons → advance-combat circular import; squadron
  // recall on destroyed carriers loses surviving fighters (no rebuild
  // credit), which matches the design intent that squadron survival is
  // tied to the carrier's survival.
  const orphanSquadrons: ShipInstanceId[] = [];
  for (const ship of state.ships.values()) {
    if (ship.parentCarrierShipId === id) orphanSquadrons.push(ship.id);
  }
  for (const sqId of orphanSquadrons) {
    state.ships.delete(sqId);
    for (const empire of state.empires.values()) {
      const idx = empire.shipIds.indexOf(sqId);
      if (idx >= 0) empire.shipIds.splice(idx, 1);
    }
    for (const teamMap of state.teamVision.values()) {
      teamMap.delete(sqId);
    }
  }
}

/**
 * Compatibility re-export: unused `slot` parameter kept so future changes
 * to WeaponSlotState that need a dedicated init helper have a single
 * obvious place to land. Exported-but-inert is cheaper than searching the
 * codebase when the time comes.
 */
export function isSlotReady(slot: WeaponSlotState): boolean {
  return slot.cooldownTicks <= 0 && slot.ammoRemaining > 0;
}
